/**
 * Scheduler board theme — Figma Screen 1103 (calendar) / 1104 (timeline).
 *
 * Pure module: tone constants, HTML-string template helpers for DHTMLX
 * `templates={{...}}`, one sx object for the scheduler wrapper Box, and the
 * gesture config every board passes in `config={{...}}`.
 * Hexes mirror `src/design/aeronet-tokens.css` (not imported — CSS-in-JS here).
 *
 * @figma https://www.figma.com/design/XZd3Kyn3b7ziLnELrO4TNk/Aeronet-Design-5.0?node-id=711-2823
 * Pattern 840 Calendar Board and Pattern 841 Calendar Day (1502:33594).
 * Module=Flights uses flight event templates over linked booking spans;
 * Module=Bookings uses booking event templates. Quick Filters select layers.
 */

import { format, isSameDay } from "date-fns";

export interface EventTone {
  accent: string;
  tint: string;
  text: string;
}

/** Event tones in rotation order: teal, orange, violet, green, cyan, olive, red. */
export const EVENT_TONES: EventTone[] = [
  { accent: "#018a90", tint: "#e2f1f1", text: "#016a6f" },
  { accent: "#d36f2c", tint: "#f6ece0", text: "#9c5a2f" },
  { accent: "#7c4dbe", tint: "#f0e9f9", text: "#5b3590" },
  { accent: "#059669", tint: "#e7f5ee", text: "#047857" },
  { accent: "#0e7490", tint: "#e0f2f7", text: "#0e7490" },
  { accent: "#8a6d18", tint: "#f5f1e0", text: "#6b5412" },
  { accent: "#c13734", tint: "#f8e7e6", text: "#9a2b29" },
];

/** Grey tone — overlays only (never assigned by `toneForIndex`). */
export const OVERLAY_TONE: EventTone = {
  accent: "#9e9e9e",
  tint: "#f5f5f5",
  text: "#616161",
};

export function toneForIndex(i: number): EventTone {
  if (!Number.isFinite(i) || i < 0) return EVENT_TONES[0];
  return EVENT_TONES[Math.floor(i) % EVENT_TONES.length];
}

/**
 * The DHTMLX `config` keys that decide what a pointer gesture on a board may
 * do. Spread into every board's `config` (and re-applied onto
 * `instance.config` where a board updates it after mount).
 *
 * ⚠️ NEVER `readonly: true` to withhold writes. The library's
 * `_on_dbl_click` returns BEFORE it calls `onDblClick` when `config.readonly`
 * is set (dhtmlxscheduler.react.es.js, ~L3499-3501 in 7.2.x), so a readonly
 * board cannot be double-clicked open at all — the legacy gesture every board
 * here keeps (PATTERNS.md, "single click selects, double click opens"). The
 * Flights calendar shipped exactly that: `readonly: true`, and a dead
 * double-click for every user in every view (H252). Withhold the writes by
 * name instead:
 *
 *   drag_move / drag_resize — only when the caller may move a record.
 *   drag_create             — only when the caller may create one.
 *   dblclick_create         — never; empty-cell create is `onEmptyClick`.
 *   details_on_dblclick     — never; the board's `onDblClick` opens the record.
 *   select = selectBar      — the library's own select bar: Details / Edit /
 *                             Delete icons on a clicked event in the vertical
 *                             views. Bookings keeps it for a reader who may
 *                             edit or create (Aaron, 2026-09-30: "Keep the icon
 *                             bar") — exactly who `readonly` used to let
 *                             select. Flights never had it and stays without.
 *                             Every board still draws its own selection ring
 *                             through `event_class`.
 */
export function boardGestureConfig(o: {
  canMove: boolean;
  canCreate: boolean;
  selectBar: boolean;
}) {
  return {
    readonly: false,
    select: o.selectBar,
    details_on_dblclick: false,
    dblclick_create: false,
    drag_move: o.canMove,
    drag_resize: o.canMove,
    drag_create: o.canCreate,
  };
}

// ------------------------------------------------ events with no times

/**
 * What a record with no recorded times says, wherever its time would go —
 * block, bar and tooltip (Aaron, 2026-09-30, H252: "should be all day events").
 */
export const NO_TIMES_TEXT = "No times recorded";

/**
 * The dates that make DHTMLX draw a record as ALL-DAY: 00:00 on its first day
 * to 00:00 after its last. With `all_timed: false` and `multi_day: true` (the
 * library default) the day-column views put such an event in the all-day row
 * above the hours, and a timeline draws it as a full-day band.
 *
 * It used to be drawn across the visible hours instead — DEFAULT_FIRST_HOUR ..
 * DEFAULT_LAST_HOUR, 07:00-23:00 — and read as a record booked for 07:00-23:00.
 *
 * `toIso` before `fromIso`, blank or a zero-date means a one-day record.
 * Null for an unusable `fromIso`.
 */
export function allDayDates(
  fromIso: string | null | undefined,
  toIso?: string | null
): { start: Date; end: Date } | null {
  const from = String(fromIso ?? "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(from) || from === "0000-00-00") return null;
  const rawTo = String(toIso ?? "").slice(0, 10);
  const to = /^\d{4}-\d{2}-\d{2}$/.test(rawTo) && rawTo >= from ? rawTo : from;
  const start = new Date(`${from}T00:00:00`);
  const end = new Date(`${to}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  end.setDate(end.getDate() + 1);
  return { start, end };
}

/**
 * A one-line bar's title for an all-day record. The 24/34px bars clip a second
 * line (`.an-bar` overflow hidden), so the reason rides in the title itself.
 */
export function allDayBarTitle(title: string): string {
  return `${title} · ${NO_TIMES_TEXT}`;
}

/** Escapes text AND attribute values. */
export function escapeHtml(s: unknown): string {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function toneVars(tone: EventTone, withTint: boolean): string {
  const parts = [`--an-ev-accent:${tone.accent}`];
  if (withTint) parts.push(`--an-ev-tint:${tone.tint}`);
  parts.push(`--an-ev-text:${tone.text}`);
  return escapeHtml(parts.join(";"));
}

export function eventBlockHtml(o: {
  title: string;
  id?: string;
  time?: string;
  tone: EventTone;
  hasFlights?: boolean;
}): string {
  const flightIcon = o.hasFlights
    ? `<span class="an-ev__flight-icon" title="Linked flight">✈ </span>`
    : "";
  const id = o.id ? `<div class="an-ev__id">${escapeHtml(o.id)}</div>` : "";
  const time = o.time
    ? `<div class="an-ev__rule"></div><div class="an-ev__time">${escapeHtml(o.time)}</div>`
    : "";
  return (
    `<div class="an-ev" style="${toneVars(o.tone, true)}">` +
    `<span class="an-ev__bar"></span>` +
    `<div class="an-ev__text">` +
    `<div class="an-ev__title">${flightIcon}${escapeHtml(o.title)}</div>${id}${time}` +
    `</div></div>`
  );
}

export function timelineBarHtml(o: {
  title: string;
  time?: string;
  tone: EventTone;
  hasFlights?: boolean;
}): string {
  const flightIcon = o.hasFlights
    ? `<span class="an-bar__flight-icon" title="Linked flight">✈ </span>`
    : "";
  const time = o.time
    ? `<div class="an-bar__time">${escapeHtml(o.time)}</div>`
    : "";
  return (
    `<div class="an-bar" style="${toneVars(o.tone, false)}">` +
    `<div class="an-bar__title">${flightIcon}${escapeHtml(o.title)}</div>${time}</div>`
  );
}

export function dayHeaderHtml(date: Date, today?: Date): string {
  const cls = isSameDay(date, today ?? new Date()) ? " an-dh--today" : "";
  return (
    `<div class="an-dh${cls}">` +
    `<span class="an-dh__weekday">${escapeHtml(format(date, "EEE"))}.</span>` +
    `<span class="an-dh__day">${escapeHtml(format(date, "d"))}</span></div>`
  );
}

export function timelineDayHeaderHtml(date: Date, today?: Date): string {
  const cls = isSameDay(date, today ?? new Date()) ? " an-tdh--today" : "";
  const weekday = format(date, "EEE").charAt(0);
  return (
    `<div class="an-tdh${cls}">` +
    `<span class="an-tdh__weekday">${escapeHtml(weekday)}</span>` +
    `<span class="an-tdh__day">${escapeHtml(format(date, "d"))}</span></div>`
  );
}

export function sectionLabelHtml(o: {
  title: string;
  subtitle?: string;
}): string {
  const sub = o.subtitle
    ? `<div class="an-sec__sub">${escapeHtml(o.subtitle)}</div>`
    : "";
  return (
    `<div class="an-sec">` +
    `<div class="an-sec__title">${escapeHtml(o.title)}</div>${sub}</div>`
  );
}

/**
 * Class on a DISPLAY ▾ overlay band (a DHTMLX marked timespan).
 *
 * Colour has to arrive as a class: the timeline renderer assigns the block's
 * `style.cssText` wholesale (`dhtmlxscheduler.react.es.js:12810`), so an inline
 * per-band style would be discarded. One modifier per tone, plus grey for a
 * tone outside the rotation.
 */
export const OVERLAY_CLASS = "an-ov";

function rgba(hex: string, a: number): string {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.replace(/(.)/g, "$1$1") : h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

export const overlayToneRule = (accent: string) => ({
    // Patterns 840/841: the booking is a quiet span behind the flight cards.
    // Its accent bar belongs to the booking-card variant, not the backdrop.
    background: rgba(accent, 0.1),
});

function overlayToneRules(): Record<string, object> {
  const out: Record<string, object> = {
    [`& .${OVERLAY_CLASS}`]: {
      borderRadius: "6px",
      // The band is scenery. Clicks belong to the row underneath it.
      pointerEvents: "none",
    },
    [`& .${OVERLAY_CLASS}--grey`]: overlayToneRule(OVERLAY_TONE.accent),
  };
  EVENT_TONES.forEach((t, i) => {
    out[`& .${OVERLAY_CLASS}--${i}`] = overlayToneRule(t.accent);
  });
  return out;
}

/** Applied to the Box wrapping `<ReactScheduler>`; CSS vars cascade into `.dhx_cal_container`. */
export const schedulerBoardSx = {
  ...overlayToneRules(),
  fontFamily: "Roboto, Helvetica, Arial, sans-serif",
  "--dhx-scheduler-font-family": "Roboto, Helvetica, Arial, sans-serif",
  "--dhx-scheduler-container-background": "#ffffff",
  "--dhx-scheduler-default-border": "1px solid rgba(0,0,0,0.06)",
  "--dhx-scheduler-halfhour-border": "1px solid transparent",
  "--dhx-scheduler-header-border": "1px solid transparent",
  "--dhx-scheduler-scale-color": "#616161",
  "--dhx-scheduler-hours-font-size": "12px",
  "--dhx-scheduler-hours-font-weight": "400",
  "--dhx-scheduler-timescale-background": "transparent",
  "--dhx-scheduler-timescale-today-background": "transparent",
  "--dhx-scheduler-event-border": "none",
  "--dhx-scheduler-border-radius": "4px",
  "--dhx-scheduler-base-colors-primary": "#008e92",
  "--dhx-scheduler-base-colors-border": "rgba(0,0,0,0.06)",
  "--dhx-scheduler-base-colors-text-base": "#616161",

  "& .dhx_cal_container": {
    border: "none",
    borderRadius: 0,
    background: "#fff",
  },
  // License #44427373 — suppress DHTMLX evaluation watermark popup container
  "& .dhx_cal_container > div:not([class])": {
    width: "0 !important",
    height: "0 !important",
    maxWidth: "0 !important",
    maxHeight: "0 !important",
    minWidth: "0 !important",
    minHeight: "0 !important",
    overflow: "hidden !important",
    clipPath: "inset(100%) !important",
    transform: "scale(0) !important",
    pointerEvents: "none !important",
    position: "absolute !important",
    top: "-9999px !important",
    left: "-9999px !important",
    opacity: "0.001 !important",
  },
  "& .dhx_cal_navline": { display: "none !important", height: "0 !important" },
  "& .dhx_cal_header": { border: "none" },
  // DHTMLX ships `.dhx_scale_bar` as flex/center/baseline, which shrink-wraps
  // the pill to its text — the Figma header fills the column, left-aligned.
  "& .dhx_scale_bar": {
    background: "transparent",
    border: "none",
    padding: "0 4px 0 0",
    textAlign: "left",
    justifyContent: "flex-start",
    alignItems: "stretch",
  },

  // Calendar day header (Figma Component 69)
  "& .an-dh": {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    height: "58px",
    padding: "0 20px",
    background: "#f0f0f0",
    borderRadius: "2px",
    boxSizing: "border-box",
    flex: "1 1 auto",
    width: "100%",
  },
  "& .an-dh--today": { background: "#e4f1f1" },
  "& .an-dh__weekday": { font: "400 13px/15px Roboto", color: "#616161" },
  "& .an-dh__day": { font: "500 20px/23px Roboto", color: "#3d3d3d" },
  "& .an-dh--today .an-dh__weekday, & .an-dh--today .an-dh__day": {
    color: "#008e92",
  },

  // DHTMLX centres the hour label in the gutter (`align-items:center` on a
  // column flex); Figma pins it to the gutter's left edge.
  "& .dhx_scale_hour": {
    color: "#616161",
    font: "400 12px Roboto",
    background: "transparent",
    border: "none",
    textAlign: "left",
    paddingLeft: 0,
    alignItems: "flex-start",
    justifyContent: "flex-start",
  },
  "& .dhx_scale_holder": { borderLeft: "1px solid rgba(0,0,0,0.06)" },

  // Day-column event (Figma Component 68)
  "& .dhx_cal_event": {
    background: "transparent",
    border: "none",
    boxShadow: "none",
  },
  "& .dhx_cal_event .dhx_title": { display: "none" },
  "& .dhx_cal_event .dhx_body": {
    padding: 0,
    height: "100%",
    background: "transparent",
    border: "none",
    overflow: "hidden",
  },
  "& .an-ev": {
    display: "flex",
    gap: "6px",
    height: "100%",
    padding: "4px 8px 4px 6px",
    borderRadius: "4px",
    boxSizing: "border-box",
    background: "var(--an-ev-tint, #f5f5f5)",
    overflow: "hidden",
  },
  "& .an-ev__bar": {
    flex: "0 0 3px",
    borderRadius: "2px",
    background: "var(--an-ev-accent)",
  },
  "& .an-ev__title": {
    font: "500 13px/18px Roboto",
    color: "var(--an-ev-text)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .an-ev__id, & .an-ev__time": {
    font: "400 11px/18px Roboto",
    color: "#9e9e9e",
  },
  "& .an-ev__rule": {
    height: "1px",
    background: "rgba(0,0,0,0.08)",
    margin: "2px 0",
  },

  "& .flight_selected .an-ev, & .booking_selected .an-ev": {
    outline: "2px solid #008e92",
    outlineOffset: "-2px",
  },
  "& .flight_selected .an-bar, & .booking_selected .an-bar": {
    outline: "2px solid #008e92",
    outlineOffset: "-2px",
  },
  "& .an-bar__flight-icon, & .an-ev__flight-icon": {
    display: "inline-block",
    marginRight: "4px",
    fontSize: "11px",
    lineHeight: 1,
    verticalAlign: "middle",
  },

  // Timeline (Figma Pattern 810)
  "& .dhx_timeline_scale_header": {
    background: "#f5f5f5",
    borderRight: "1px solid #e0e0e0",
    borderBottom: "1px solid #e0e0e0",
    color: "#616161",
    font: "600 11px/13px Roboto",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "center",
    paddingLeft: "16px",
    boxSizing: "border-box",
  },
  // DHTMLX centres the entity column (`text-align:center`, and `justify-content:
  // center` on the flex `.dhx_matrix_scell_columns` variant); Figma left-aligns it.
  "& .dhx_matrix_scell": {
    background: "#fff",
    borderRight: "1px solid #e0e0e0",
    borderBottom: "1px solid #e0e0e0",
    textAlign: "left",
    paddingLeft: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    boxSizing: "border-box",
  },
  "& .dhx_matrix_scell div": { textAlign: "left" },
  "& .dhx_timeline_scale_header .dhx_matrix_scell": { background: "#f5f5f5" },
  "& .dhx_matrix_cell": {
    borderLeft: "1px solid #eeeeee",
    borderBottom: "1px solid #e0e0e0",
  },
  "& .dhx_matrix_line": { borderColor: "#e0e0e0" },
  "& .dhx_cal_event_line": {
    background: "transparent",
    border: "none",
    padding: 0,
    boxShadow: "none",
  },
  "& .an-bar": {
    height: "100%",
    boxSizing: "border-box",
    padding: "5px 6px 0 8px",
    borderLeft: "3px solid var(--an-ev-accent)",
    borderRadius: "3px",
    background: "color-mix(in srgb, var(--an-ev-accent) 16%, white)",
    overflow: "hidden",
  },
  "& .an-bar__title": {
    font: "500 11px/13px Roboto",
    color: "var(--an-ev-accent)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .an-bar__time": { font: "400 10px/12px Roboto", color: "#616161" },

  "& .an-sec": {
    textAlign: "left",
    paddingRight: "12px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .an-sec__title": {
    font: "500 13px/15px Roboto",
    color: "#212121",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .an-sec__sub": { font: "400 11px/13px Roboto", color: "#616161" },

  "& .an-tdh": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
    width: "100%",
    flex: "1 1 auto",
    textAlign: "center",
  },
  "& .an-tdh__weekday": { font: "500 11px/13px Roboto", color: "#616161" },
  "& .an-tdh__day": { font: "700 12px/14px Roboto", color: "#212121" },
  "& .an-tdh--today": { background: "#e4f1f1" },
  "& .an-tdh--today .an-tdh__weekday, & .an-tdh--today .an-tdh__day": {
    color: "#008e92",
  },
};
